import { useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"

// Redux hooks and selectors
import { useAppSelector } from "@/reducers/hooks"
import { selectChatSettings } from "@/reducers/chatsSettingsSelectors"
import { selectChatCounts } from "@/reducers/chatsSliceSelectors"
import { selectAccountChats } from "@/reducers/accountChatsSlice"
import { selectSyncFailedRoomJids } from "@/reducers/roomSyncStatusSelectors"
import { messageStatuses } from "@/reducers/selectors"
import { countPinnedMessagesFromChatCreator } from "@/reducers/pinnedSliceSelectors"
import { messageAuthorName } from "@/reducers/profilesSliceSelectors" // Types and enums
import { MessageDisplayType } from "@/reducers/messagesSlice"
import { EnhancedChat } from "@/components/ChatList" // Hooks and context
import { XmppContext } from "@/stream/xmppClient"
import { useLastChatEvent } from "@/hooks/useLastChatsEvent"
import useImageCache from "@/hooks/useImageCache"
import { useDriverRoomIssues } from "@/hooks/useDriverRoomIssues"
import useRouteBadges from "@/components/chatCard/useRouteBadges" // Utilities and helpers
import ChatHelpers from "@/lib/chatHelpers"
import MessageHelpers from "@/lib/messageHelpers"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { relativeTimeLabel } from "@/components/chatCard/utils"
import {
  formatLastEventText,
  resolveLastEventAuthorName,
} from "@/lib/lastEventText"
import { useFeatureFlagEnabled } from "posthog-js/react"

/**
 * Custom hook that aggregates all data needed for rendering a chat card.
 * Combines chat metadata, driver info, route data, and message status.
 */
const useChatCardData = (chat: EnhancedChat) => {
  const { jid, name, isArchived = false } = chat
  const { myJid } = useContext(XmppContext)
  const { t } = useTranslation()

  // Core chat data selectors
  const accountChats = useAppSelector(selectAccountChats)
  const syncFailedRoomJids = useAppSelector(selectSyncFailedRoomJids)
  const chatSettings = useAppSelector(selectChatSettings(jid))
  const lastChatEvent = useLastChatEvent(jid)
  const statuses = useAppSelector(messageStatuses(chat), shallowEqual)
  const driverRoomIssues = useDriverRoomIssues(chat.jid)
  const chatCardConfigWarningEnabled =
    useFeatureFlagEnabled("chat-card-warning")

  // Everything about the truck rather than the conversation.
  const { driverParticipant, ...routeBadges } = useRouteBadges(jid)

  // Chat counts for unread messages - memoized selector
  const chatCountsSelector = useMemo(
    () => selectChatCounts(myJid.toString()),
    [myJid],
  )
  const chatCounts = useAppSelector(chatCountsSelector, shallowEqual)

  // Pinned messages count - memoized selector
  const pinnedCountSelector = useMemo(
    () => countPinnedMessagesFromChatCreator(jid),
    [jid],
  )
  const pinnedCount = useAppSelector(pinnedCountSelector)

  // Compute derived values with useMemo for performance
  const avatarPhoto = useMemo(
    () => participantPhotoToSrc(driverParticipant?.photo),
    [driverParticipant?.photo],
  )

  const avatarName = useMemo(
    () =>
      driverParticipant
        ? `${driverParticipant.firstName} ${driverParticipant.lastName}`
        : name,
    [driverParticipant, name],
  )

  const isLastEventToDriver = useMemo(
    () =>
      lastChatEvent &&
      driverParticipant &&
      !MessageHelpers.hasComeFromJid(lastChatEvent, driverParticipant.jid),
    [lastChatEvent, driverParticipant],
  )

  const authorName = useAppSelector(messageAuthorName(lastChatEvent, myJid))
  const lastEventAuthorName = resolveLastEventAuthorName({
    authorName,
    event: lastChatEvent,
    draftMessage: chat?.draftMessage,
  })

  // Get status of last event (if it's a valid message type)
  const lastEventStatus = useMemo(() => {
    if (
      !lastChatEvent ||
      lastChatEvent.displayType === MessageDisplayType.Reaction ||
      lastChatEvent.retracted
    ) {
      return undefined
    }
    return statuses[lastChatEvent.id]
  }, [lastChatEvent, statuses])

  // Format the last event text for display
  const lastEventText = useMemo(
    () =>
      chat
        ? formatLastEventText({
            event: lastChatEvent,
            myJid,
            accountChat: accountChats[jid],
            draftMessage: chat.draftMessage,
            t,
          })
        : "",
    [lastChatEvent, chat, myJid, accountChats, jid, t],
  )

  // Avatar color computation with fallback logic
  const avatarColor = useMemo(() => {
    const chatColor = ChatHelpers.getChatColor(chat)
    if (chatColor) return chatColor

    // Fallback colors based on chat state
    if (isArchived) return "#cccccc"
    if (accountChats[jid]?.group === "shared_driver") return "#ffecb8"

    return "var(--defaultAvatarColor)"
  }, [chat, isArchived, accountChats, jid])

  // Sync status check
  const hasSyncFailed = useMemo(
    () => syncFailedRoomJids.some((room) => room.jid === chat.jid),
    [chat.jid, syncFailedRoomJids],
  )

  // Image caching for avatar photo
  const { cachedSrc, isLoading } = useImageCache(avatarPhoto)

  // Basic chat state
  const isMuted = chatSettings?.muted ?? false
  const withExternalContact = ChatHelpers.isChatWithExternalContact(chat)
  const unreadCount = chatCounts[jid] ?? 0

  // Return all computed values for the chat card
  return {
    // Avatar data
    avatarName: avatarPhoto ? cachedSrc : avatarName,
    avatarPhoto: cachedSrc,
    avatarPhotoLoading: isLoading,
    avatarColor,

    // Last event data
    lastEventText,
    lastEventAuthorName,
    lastEventStatus,
    lastEventRelativeTime: relativeTimeLabel(lastChatEvent?.timestamp, t),
    isLastEventToDriver: Boolean(isLastEventToDriver),

    // Route and tracking data
    ...routeBadges,

    // Chat state flags
    isMuted,
    withExternalContact,
    isArchived,
    hasSyncFailed,
    unreadCount,
    hasPinnedMessages: pinnedCount > 0,

    // Driver room config issues — single triangle on the row; tooltip
    // (built by DriverRoomIssueIndicator) lists every active issue.
    // Gated behind the chat-card-warning flag.
    hasConfigIssues: chatCardConfigWarningEnabled
      ? (driverRoomIssues?.hasAnyIssue ?? false)
      : false,
    configIssues: chatCardConfigWarningEnabled ? driverRoomIssues : null,
  }
}

export default useChatCardData
